import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react'
import type { Block, Variable, DemoBackground, DemoTheme } from '../types'
import BlockRenderer from '../../renderer/BlockRenderer'
import { groupBlockRows } from '../../renderer/groupBlockRows'
import { GripVertical, Monitor, Tablet, Smartphone, ZoomIn, Eye, EyeOff, Trash2 } from 'lucide-react'
import { evaluateVisibility } from '../../lib/visibility'
import { loadGoogleFont, getFontFamily } from './ThemeEditor'
import InlineCodeEditor from './InlineCodeEditor'

interface StepVisibility {
  controlled: Set<string>
  visible: Set<string>
}

interface CanvasProps {
  blocks: Block[]
  variables: Variable[]
  variableValues: Record<string, number | string | boolean>
  selectedBlockId: string | null
  onSelectBlock: (id: string) => void
  onMoveBlock: (from: number, to: number) => void
  onRemoveBlock: (id: string) => void
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
  onSetVariableValue: (name: string, value: number | string | boolean) => void
  saving?: boolean
  background?: DemoBackground
  theme?: DemoTheme
  stepVisibility?: StepVisibility
}

const DEVICE_WIDTHS = {
  desktop: 'max-w-4xl',
  tablet: 'max-w-[768px]',
  mobile: 'max-w-[375px]',
}

function getBackgroundStyle(background?: DemoBackground): React.CSSProperties {
  if (!background) return {}
  switch (background.type) {
    case 'solid': return { backgroundColor: background.value }
    case 'gradient': return { background: background.value }
    case 'image': return { backgroundImage: `url(${background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    default: return {}
  }
}

function getBackgroundClass(background?: DemoBackground): string {
  if (!background || background.type !== 'animated') return ''
  return background.value
}

export default function Canvas({
  blocks,
  variables,
  variableValues,
  selectedBlockId,
  onSelectBlock,
  onMoveBlock,
  onRemoveBlock,
  onUpdateBlock,
  onSetVariableValue,
  saving,
  background,
  theme,
  stepVisibility,
  canvasContentRef,
}: CanvasProps & { canvasContentRef?: React.RefObject<HTMLDivElement | null> }) {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)
  const [activeDevice, setActiveDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop')
  const [previewMode, setPreviewMode] = useState(false)
  const ghostRef = useRef<HTMLDivElement | null>(null)

  // Clean up ghost element on unmount
  useEffect(() => {
    return () => {
      if (ghostRef.current && document.body.contains(ghostRef.current)) {
        document.body.removeChild(ghostRef.current)
        ghostRef.current = null
      }
    }
  }, [])

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    if (previewMode) return
    setDragIndex(index)
    const target = e.currentTarget as HTMLElement
    const ghost = target.cloneNode(true) as HTMLDivElement
    ghost.style.opacity = '0.8'
    ghost.style.transform = 'rotate(1deg)'
    ghost.style.position = 'absolute'
    ghost.style.top = '-9999px'
    ghost.style.width = `${target.offsetWidth}px`
    document.body.appendChild(ghost)
    ghostRef.current = ghost
    e.dataTransfer.setDragImage(ghost, e.nativeEvent.offsetX, e.nativeEvent.offsetY)
    e.dataTransfer.effectAllowed = 'move'
  }, [previewMode])

  const handleDragOver = useCallback((e: React.DragEvent, index: number, inRow?: boolean) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    if (inRow) {
      // Horizontal: left/right detection within a grid row
      const midX = rect.left + rect.width / 2
      const dropIdx = e.clientX < midX ? index : index + 1
      setDropTargetIndex(dropIdx)
    } else {
      const midY = rect.top + rect.height / 2
      const dropIdx = e.clientY < midY ? index : index + 1
      setDropTargetIndex(dropIdx)
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (dragIndex !== null && dropTargetIndex !== null) {
        const adjustedTarget = dropTargetIndex > dragIndex ? dropTargetIndex - 1 : dropTargetIndex
        if (adjustedTarget !== dragIndex) {
          onMoveBlock(dragIndex, adjustedTarget)
        }
      }
      setDragIndex(null)
      setDropTargetIndex(null)
    },
    [dragIndex, dropTargetIndex, onMoveBlock]
  )

  const handleDragEnd = useCallback(() => {
    setDragIndex(null)
    setDropTargetIndex(null)
    if (ghostRef.current && document.body.contains(ghostRef.current)) {
      document.body.removeChild(ghostRef.current)
      ghostRef.current = null
    }
  }, [])

  const handleDragLeave = useCallback(() => {
    setDropTargetIndex(null)
  }, [])

  const accent = theme?.accent || '#5048e5'
  const bgStyle = getBackgroundStyle(background)
  const bgClass = getBackgroundClass(background)
  const fontFamily = getFontFamily(theme?.font)

  // Load Google Font when theme changes
  useEffect(() => {
    if (theme?.font) loadGoogleFont(theme.font)
  }, [theme?.font])

  return (
    <section className="flex-1 bg-[#F3F4F6] p-8 overflow-y-auto relative">
      <div className={`${DEVICE_WIDTHS[activeDevice]} mx-auto transition-all duration-300`}>
        {/* Editor Canvas Toolbar */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
              {(['desktop', 'tablet', 'mobile'] as const).map((device) => {
                const Icon = device === 'desktop' ? Monitor : device === 'tablet' ? Tablet : Smartphone
                return (
                  <button
                    key={device}
                    onClick={() => setActiveDevice(device)}
                    className={`p-1.5 rounded transition-all ${
                      activeDevice === device
                        ? 'bg-slate-100 text-slate-900'
                        : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                )
              })}
            </div>
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all shadow-sm ${
                previewMode
                  ? 'bg-[#5048e5] text-white shadow-[#5048e5]/25 hover:bg-[#5048e5]/90'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-[#5048e5]/30 hover:text-[#5048e5] hover:shadow-md'
              }`}
            >
              {previewMode ? (
                <>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                  </span>
                  Live
                  <EyeOff className="h-3.5 w-3.5 opacity-60" />
                </>
              ) : (
                <>
                  <Eye className="h-3.5 w-3.5" />
                  Preview as visitor
                </>
              )}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 italic">
              {saving ? 'Saving...' : 'Auto-saved'}
            </span>
            <button className="text-slate-600 hover:text-[#5048e5] flex items-center gap-1 transition-colors">
              <ZoomIn className="h-4 w-4" />
              <span className="text-xs font-medium">100%</span>
            </button>
          </div>
        </div>

        {/* Canvas Content */}
        <div
          ref={canvasContentRef}
          className={`bg-white rounded-xl shadow-xl min-h-[600px] border border-slate-200 overflow-hidden ${bgClass}`}
          style={{ ...bgStyle, '--accent': accent, ...(fontFamily ? { fontFamily } : {}) } as React.CSSProperties}
        >
          {blocks.length === 0 ? (
            <div className="flex items-center justify-center min-h-[600px]">
              <div className="text-center text-slate-400">
                <p className="text-lg font-medium mb-2">No blocks yet</p>
                <p className="text-sm">Add blocks from the library on the left.</p>
              </div>
            </div>
          ) : (
            <CanvasBlocks
              blocks={blocks}
              variables={variables}
              variableValues={variableValues}
              selectedBlockId={previewMode ? null : selectedBlockId}
              previewMode={previewMode}
              dragIndex={dragIndex}
              dropTargetIndex={dropTargetIndex}
              onSelectBlock={onSelectBlock}
              onRemoveBlock={onRemoveBlock}
              onUpdateBlock={onUpdateBlock}
              onSetVariableValue={onSetVariableValue}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
              stepVisibility={stepVisibility}
            />
          )}
        </div>
      </div>
    </section>
  )
}

/* ========== CanvasBlocks — grid-aware block rendering ========== */

interface CanvasBlocksProps {
  blocks: Block[]
  variables: Variable[]
  variableValues: Record<string, number | string | boolean>
  selectedBlockId: string | null
  previewMode: boolean
  dragIndex: number | null
  dropTargetIndex: number | null
  onSelectBlock: (id: string) => void
  onRemoveBlock: (id: string) => void
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
  onSetVariableValue: (name: string, value: number | string | boolean) => void
  onDragStart: (e: React.DragEvent, index: number) => void
  onDragOver: (e: React.DragEvent, index: number, inRow?: boolean) => void
  onDragLeave: () => void
  onDragEnd: () => void
  onDrop: (e: React.DragEvent) => void
  stepVisibility?: StepVisibility
}

function CanvasBlocks({
  blocks, variables, variableValues, selectedBlockId, previewMode,
  dragIndex, dropTargetIndex,
  onSelectBlock, onRemoveBlock, onUpdateBlock, onSetVariableValue,
  onDragStart, onDragOver, onDragLeave, onDragEnd, onDrop,
  stepVisibility,
}: CanvasBlocksProps) {
  // In preview mode, filter out hidden blocks (same as renderer)
  const displayBlocks = useMemo(() => {
    if (!previewMode) return blocks
    let filtered = blocks
    if (stepVisibility) {
      filtered = filtered.filter((b) => !stepVisibility.controlled.has(b.id) || stepVisibility.visible.has(b.id))
    }
    filtered = filtered.filter((b) => evaluateVisibility(b.visibility, variableValues))
    return filtered
  }, [blocks, previewMode, stepVisibility, variableValues])

  const rows = useMemo(() => groupBlockRows(displayBlocks), [displayBlocks])

  // Map block id → original index in flat array (needed for DnD)
  const indexMap = useMemo(() => {
    const m = new Map<string, number>()
    blocks.forEach((b, i) => m.set(b.id, i))
    return m
  }, [blocks])

  return (
    <div
      className="p-10 space-y-2"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      {rows.map((row) => {
        const isGrid = row.length > 1 || (row[0].span && row[0].span < 3)

        return (
          <div
            key={row.map((b) => b.id).join('-')}
            className={isGrid ? 'grid grid-cols-3 gap-2 items-stretch' : ''}
          >
            {row.map((block) => {
              const index = indexMap.get(block.id) ?? 0
              const span = block.span || 3

              if (previewMode) {
                return (
                  <div key={block.id} className="py-2" style={isGrid ? { gridColumn: `span ${span}` } : undefined}>
                    <BlockRenderer
                      block={block}
                      variableValues={variableValues}
                      variables={variables}
                      onSetVariableValue={onSetVariableValue}
                    />
                  </div>
                )
              }

              const isSelected = selectedBlockId === block.id
              const isControlled = stepVisibility?.controlled.has(block.id)
              const isStepVisible = stepVisibility?.visible.has(block.id)
              const isStepDimmed = isControlled && !isStepVisible
              const isVisibilityHidden = !evaluateVisibility(block.visibility, variableValues)
              const isDimmed = isStepDimmed || isVisibilityHidden
              const showDropBefore = dropTargetIndex === index && dragIndex !== null && dragIndex !== index

              const showDropLeft = isGrid && showDropBefore
              const showDropRight = isGrid && dropTargetIndex === index + 1 && dragIndex !== null && dragIndex !== index

              return (
                <React.Fragment key={block.id}>
                  {/* Drop indicator: horizontal line between rows */}
                  {!isGrid && (
                    <div className={`transition-all duration-200 ${showDropBefore ? 'h-0.5 bg-[#5048e5] rounded-full my-2' : 'h-0'}`} />
                  )}
                  <div
                    draggable
                    onDragStart={(e) => onDragStart(e, index)}
                    onDragOver={(e) => onDragOver(e, index, isGrid)}
                    onDragLeave={onDragLeave}
                    onDragEnd={onDragEnd}
                    onClick={() => onSelectBlock(block.id)}
                    className={`group relative transition-all duration-200 ${
                      isSelected
                        ? 'p-6 border-2 border-[#5048e5] ring-4 ring-[#5048e5]/10 rounded-xl bg-slate-50/50'
                        : 'p-4 border border-transparent hover:border-slate-200 rounded-xl cursor-pointer'
                    } ${dragIndex === index ? 'opacity-30 scale-[0.98]' : ''} ${isDimmed ? 'opacity-40' : ''} ${
                      showDropLeft ? 'border-l-4 border-l-[#5048e5]' : ''
                    } ${showDropRight ? 'border-r-4 border-r-[#5048e5]' : ''}`}
                    style={isGrid ? { gridColumn: `span ${span}` } : undefined}
                  >
                    {isSelected && (
                      <div className="absolute -top-3 left-4 bg-[#5048e5] text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase">
                        {block.type}{span < 3 ? ` · ${span}/3` : ''}
                      </div>
                    )}

                    {/* Span indicator (when not selected) */}
                    {!isSelected && span < 3 && (
                      <div className="absolute -top-2 right-3 bg-slate-400 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                        {span}/3
                      </div>
                    )}

                    {/* Hidden badge for step-controlled or visibility-hidden blocks */}
                    {isStepDimmed && (
                      <div className="absolute -top-2 left-4 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                        Hidden by step
                      </div>
                    )}
                    {isVisibilityHidden && !isStepDimmed && (
                      <div className="absolute -top-2 left-4 bg-purple-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                        Conditionally hidden
                      </div>
                    )}

                    <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                      <GripVertical className="h-4 w-4 text-slate-400" />
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); onRemoveBlock(block.id) }}
                      className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-white/90 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-lg shadow-sm z-10"
                      title="Delete block"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                    </button>

                    <div className="pl-4">
                      {block.type === 'codeblock' ? (
                        <InlineCodeEditor block={block} onUpdateBlock={onUpdateBlock} />
                      ) : (
                        <BlockRenderer
                          block={block}
                          variableValues={variableValues}
                          variables={variables}
                          onSetVariableValue={onSetVariableValue}
                        />
                      )}
                    </div>
                  </div>
                </React.Fragment>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
